import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { BookOpen, Lock, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AccountantView = () => {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/api/mybookie/accountant/shared/${shareToken}`);
        setData(res.data);
      } catch (err) {
        console.error("AccountantView load error:", err);
        setError('Invalid or expired share link');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [shareToken]);

  const fmt = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F6F5F2]"><div className="animate-spin w-8 h-8 border-2 border-[#0A2B1D] border-t-transparent rounded-full"></div></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F6F5F2]">
      <Card className="max-w-md"><CardContent className="p-8 text-center">
        <Lock className="w-12 h-12 text-[#8A948F] mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-[#111614] mb-2">Access Denied</h2>
        <p className="text-[#5C6661]">{error}</p>
      </CardContent></Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F6F5F2]" data-testid="accountant-view">
      <header className="bg-white border-b border-[#E6E8E6] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#0A2B1D] rounded-sm flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-[#111614]">MyBookie</span>
          </div>
          <span className="px-3 py-1 bg-[#E2ECE7] text-[#0A2B1D] rounded-full text-xs font-medium">Read-Only View</span>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#111614]">{data.business?.name}</h1>
          <p className="text-[#5C6661]">Shared financial overview (read-only)</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card><CardContent className="p-4 text-center">
            <TrendingUp className="w-5 h-5 text-green-600 mx-auto mb-1" />
            <p className="text-xs text-[#5C6661]">Total Income</p>
            <p className="text-xl font-bold text-green-600">{fmt(data.summary?.total_income)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <TrendingDown className="w-5 h-5 text-red-600 mx-auto mb-1" />
            <p className="text-xs text-[#5C6661]">Total Expenses</p>
            <p className="text-xl font-bold text-red-600">{fmt(data.summary?.total_expenses)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <DollarSign className="w-5 h-5 text-[#0A2B1D] mx-auto mb-1" />
            <p className="text-xs text-[#5C6661]">Net Income</p>
            <p className="text-xl font-bold text-[#0A2B1D]">{fmt(data.summary?.net_income)}</p>
          </CardContent></Card>
        </div>

        {/* Income Table */}
        <Card>
          <CardHeader><CardTitle className="text-base">Income ({data.income?.length || 0})</CardTitle></CardHeader>
          <CardContent>
            {data.income?.length > 0 ? (
              <table className="w-full text-sm">
                <thead><tr className="border-b bg-[#F6F5F2]">
                  <th className="text-left py-2 px-3">Date</th><th className="text-left py-2 px-3">Description</th><th className="text-right py-2 px-3">Amount</th>
                </tr></thead>
                <tbody>
                  {data.income.map((inc, i) => (
                    <tr key={inc.date + inc.description + i} className="border-b"><td className="py-2 px-3">{inc.date}</td><td className="py-2 px-3">{inc.description}</td><td className="py-2 px-3 text-right text-green-600">{fmt(inc.amount)}</td></tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="text-[#5C6661] text-center py-4">No income records</p>}
          </CardContent>
        </Card>

        {/* Expenses Table */}
        <Card>
          <CardHeader><CardTitle className="text-base">Expenses ({data.expenses?.length || 0})</CardTitle></CardHeader>
          <CardContent>
            {data.expenses?.length > 0 ? (
              <table className="w-full text-sm">
                <thead><tr className="border-b bg-[#F6F5F2]">
                  <th className="text-left py-2 px-3">Date</th><th className="text-left py-2 px-3">Description</th><th className="text-left py-2 px-3">Vendor</th><th className="text-right py-2 px-3">Amount</th>
                </tr></thead>
                <tbody>
                  {data.expenses.map((exp, i) => (
                    <tr key={exp.date + exp.description + i} className="border-b"><td className="py-2 px-3">{exp.date}</td><td className="py-2 px-3">{exp.description}</td><td className="py-2 px-3 text-[#5C6661]">{exp.vendor || '-'}</td><td className="py-2 px-3 text-right text-red-600">{fmt(exp.amount)}</td></tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="text-[#5C6661] text-center py-4">No expense records</p>}
          </CardContent>
        </Card>
      </div>

      <footer className="bg-[#111614] text-[#8A948F] py-4 px-6 text-center text-xs">
        Shared via MyBookie &middot; Read-only access &middot; {new Date().getFullYear()}
      </footer>
    </div>
  );
};

export default AccountantView;
